import { useCallback, useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CalendarDays, ChevronLeft, ChevronRight, Clock, MapPin, Plus } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { CamposAgenda, FormOs } from './FormOs';
import {
  NOME_PRIORIDADE, NOME_STATUS_OS, diaCurto, diaLongo, duracaoTexto, hhmm, hora, inicioDaSemana, inicioDoDia,
  isoDia, juntarDataHora, mesmoDia, minutosDe, somaDias, textoConflitos, type ConfigAgenda, type Conflito, type OsAgenda, type Tecnico,
} from './comum';

const CONFIG_PADRAO: ConfigAgenda = { inicio: '07:00', fim: '19:00', duracao_padrao_min: 60, fuso: 'America/Sao_Paulo' };

export function Agenda() {
  const { usuario } = useSessao();
  const podeAgendar = pode(usuario, 'os.agendar');
  const podeAbrir = pode(usuario, 'os.abrir');
  const [params, setParams] = useSearchParams();
  const visao = params.get('visao') === 'semana' ? 'semana' : 'dia';
  const dataBase = useMemo(() => {
    const d = params.get('dia'); return d ? inicioDoDia(new Date(`${d}T12:00:00`)) : inicioDoDia(new Date());
  }, [params]);
  const [cfg, setCfg] = useState<ConfigAgenda>(CONFIG_PADRAO);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [agendadas, setAgendadas] = useState<OsAgenda[]>([]);
  const [fila, setFila] = useState<OsAgenda[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [modal, setModal] = useState<{ tipo: 'nova'; pre?: { responsavel_id?: string; data?: string; hora?: string } } | { tipo: 'os'; os: OsAgenda } | null>(null);

  const [de, ate] = visao === 'semana' ? [inicioDaSemana(dataBase), somaDias(inicioDaSemana(dataBase), 7)] : [dataBase, somaDias(dataBase, 1)];

  const carregar = useCallback(() => {
    setErro(null);
    Promise.all([
      api<OsAgenda[]>(`/os?de=${encodeURIComponent(de.toISOString())}&ate=${encodeURIComponent(ate.toISOString())}`),
      podeAgendar ? api<OsAgenda[]>('/os?a_agendar=true') : Promise.resolve([]),
    ]).then(([a, f]) => { setAgendadas(a.filter((o) => o.status !== 'cancelada')); setFila(f); }).catch((e) => setErro(e.message));
  }, [de.getTime(), ate.getTime(), podeAgendar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api<ConfigAgenda>('/agenda/config').then(setCfg).catch(() => {});
    api<Tecnico[]>('/tecnicos').then(setTecnicos).catch(() => {});
  }, []);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { const t = setInterval(carregar, 60_000); return () => clearInterval(t); }, [carregar]);

  const ir = (dias: number | null, v?: 'dia' | 'semana') => {
    const p = new URLSearchParams(params);
    if (dias === null) p.delete('dia'); else p.set('dia', isoDia(somaDias(dataBase, dias)));
    if (v) p.set('visao', v);
    setParams(p, { replace: true });
  };
  const passo = visao === 'semana' ? 7 : 1;
  const linhas = useMemo(() => {
    // técnicos cadastrados + quem tem OS no período mas saiu da lista (desativado)
    const extras = agendadas.filter((o) => o.responsavel_id && !tecnicos.some((t) => t.id === o.responsavel_id))
      .map((o) => ({ id: o.responsavel_id!, nome: o.responsavel ?? '—', telefone: null, placa: null }));
    const vistos = new Set<string>();
    return [...tecnicos, ...extras].filter((t) => (vistos.has(t.id) ? false : (vistos.add(t.id), true)));
  }, [tecnicos, agendadas]);

  const aoSalvar = (texto: string, conflitos: Conflito[] = []) => {
    setModal(null);
    setAviso(textoConflitos(conflitos) ?? texto);
    carregar();
  };
  useEffect(() => { if (!aviso) return; const t = setTimeout(() => setAviso(null), 8000); return () => clearTimeout(t); }, [aviso]);

  const titulo = visao === 'semana'
    ? `${diaCurto(de)} a ${diaCurto(somaDias(ate, -1))}`
    : diaLongo(dataBase);

  return (
    <>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">Operação</span>
          <h1 className="titulo">Agenda</h1>
        </div>
        {podeAbrir && <button className="btn" onClick={() => setModal({ tipo: 'nova' })}><Plus size={18} aria-hidden /> Nova OS</button>}
      </div>

      <div className="barra-agenda">
        <div className="navegacao-data">
          <button className="btn-icone-borda" onClick={() => ir(-passo)} aria-label="Anterior"><ChevronLeft size={18} /></button>
          <button className="btn-fantasma" onClick={() => ir(null)}>Hoje</button>
          <button className="btn-icone-borda" onClick={() => ir(passo)} aria-label="Próximo"><ChevronRight size={18} /></button>
          <strong className="titulo-data">{titulo}</strong>
          <input type="date" className="seletor-dia" value={isoDia(dataBase)} onChange={(e) => e.target.value && setParams((p) => { const n = new URLSearchParams(p); n.set('dia', e.target.value); return n; }, { replace: true })} aria-label="Ir para o dia" />
        </div>
        <div className="alternador" role="tablist">
          <button role="tab" aria-selected={visao === 'dia'} className={visao === 'dia' ? 'ativo' : ''} onClick={() => ir(dataBase.getTime() === inicioDoDia(new Date()).getTime() ? null : 0, 'dia')}>Dia</button>
          <button role="tab" aria-selected={visao === 'semana'} className={visao === 'semana' ? 'ativo' : ''} onClick={() => ir(0, 'semana')}>Semana</button>
        </div>
      </div>

      {erro && <p className="alerta" role="alert">{erro}</p>}
      {aviso && <p className={aviso.startsWith('Atenção') ? 'alerta alerta-aviso' : 'alerta alerta-ok'} role="status">{aviso}</p>}

      <div className={`grade-agenda${podeAgendar ? '' : ' sem-fila'}`}>
        <section className="cartao quadro-agenda">
          {linhas.length === 0 ? (
            <div className="vazio"><CalendarDays size={28} aria-hidden /><p>Nenhum técnico ativo cadastrado.</p><small className="nota">Cadastre usuários com perfil "Técnico" para montar a agenda.</small></div>
          ) : visao === 'dia' ? (
            <VisaoDia dia={dataBase} cfg={cfg} tecnicos={linhas} os={agendadas}
              aoClicarOs={(os) => setModal({ tipo: 'os', os })}
              aoClicarVazio={podeAbrir && podeAgendar ? (responsavel_id, h) => setModal({ tipo: 'nova', pre: { responsavel_id, data: isoDia(dataBase), hora: h } }) : undefined} />
          ) : (
            <VisaoSemana inicio={de} tecnicos={linhas} os={agendadas} aoClicarOs={(os) => setModal({ tipo: 'os', os })}
              aoClicarDia={(d) => setParams((p) => { const n = new URLSearchParams(p); n.set('dia', isoDia(d)); n.set('visao', 'dia'); return n; })} />
          )}
        </section>

        {podeAgendar && (
          <aside className="cartao fila-agendar" aria-label="OS a agendar">
            <h2>A agendar <span className="contador">{fila.length}</span></h2>
            {fila.length === 0 ? <p className="nota">Nenhuma OS esperando agenda.</p> : (
              <ul>
                {fila.map((o) => (
                  <li key={o.id}>
                    <button className={`cartao-os prioridade-${o.prioridade}`} onClick={() => setModal({ tipo: 'os', os: o })}>
                      <span className="linha-topo"><strong className="mono">{o.numero}</strong><span className={`pill-prioridade p-${o.prioridade}`}>{NOME_PRIORIDADE[o.prioridade]}</span></span>
                      <span className="cliente">{o.cliente} · {o.local}</span>
                      <span className="descricao">{o.descricao}</span>
                      <small>{o.tipo} · {o.data_sugerida ? <strong>sugerida {o.data_sugerida.split('-').reverse().slice(0, 2).join('/')}</strong> : `aberta ${o.criado_em ? new Date(o.criado_em).toLocaleDateString('pt-BR') : ''}`}</small>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        )}
      </div>

      <Modal titulo="Nova OS" aberto={modal?.tipo === 'nova'} aoFechar={() => setModal(null)}>
        {modal?.tipo === 'nova' && <FormOs tecnicos={tecnicos} preenchido={modal.pre} duracaoPadrao={cfg.duracao_padrao_min}
          aoSalvar={(r) => aoSalvar(`${r.numero} aberta.`, r.conflitos)} aoCancelar={() => setModal(null)} />}
      </Modal>
      <Modal titulo={modal?.tipo === 'os' ? modal.os.numero : ''} aberto={modal?.tipo === 'os'} aoFechar={() => setModal(null)}>
        {modal?.tipo === 'os' && <PainelOs os={modal.os} tecnicos={tecnicos} cfg={cfg} podeAgendar={podeAgendar}
          aoSalvar={aoSalvar} aoCancelar={() => setModal(null)} />}
      </Modal>
    </>
  );
}

// ---------- Dia: uma linha por técnico, eixo de horas ----------
function VisaoDia({ dia, cfg, tecnicos, os, aoClicarOs, aoClicarVazio }: {
  dia: Date; cfg: ConfigAgenda; tecnicos: Tecnico[]; os: OsAgenda[];
  aoClicarOs: (o: OsAgenda) => void; aoClicarVazio?: (tecnicoId: string, hora: string) => void;
}) {
  // janela visível: expediente configurado, esticada se houver OS fora dele
  let ini = minutosDe(cfg.inicio), fim = minutosDe(cfg.fim);
  for (const o of os) {
    const d = new Date(o.agendada_para!); const m = d.getHours() * 60 + d.getMinutes();
    ini = Math.min(ini, Math.floor(m / 60) * 60); fim = Math.max(fim, Math.min(24 * 60, Math.ceil((m + (o.janela_min ?? 60)) / 60) * 60));
  }
  const total = fim - ini;
  const horas = Array.from({ length: total / 60 + 1 }, (_, i) => ini + i * 60);
  const agora = new Date();
  const minAgora = agora.getHours() * 60 + agora.getMinutes();
  const pos = (m: number) => `${((m - ini) / total) * 100}%`;

  function clicarVazio(e: MouseEvent<HTMLDivElement>, tecnicoId: string) {
    if (!aoClicarVazio || e.target !== e.currentTarget) return;
    const r = e.currentTarget.getBoundingClientRect();
    const m = ini + Math.round((((e.clientX - r.left) / r.width) * total) / 30) * 30;
    aoClicarVazio(tecnicoId, `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }

  return (
    <div className="dia-rolagem">
      <div className="dia-grade">
        <div className="dia-cabecalho">
          <div className="dia-nome" />
          <div className="dia-eixo">
            {horas.map((h) => <span key={h} style={{ left: pos(h) }}>{String(h / 60).padStart(2, '0')}h</span>)}
          </div>
        </div>
        {tecnicos.map((t) => {
          const doTecnico = os.filter((o) => o.responsavel_id === t.id);
          const ocupado = doTecnico.reduce((s, o) => s + (o.janela_min ?? 60), 0);
          const { faixa, faixas } = empilhar(doTecnico);
          return (
            <div className="dia-linha" key={t.id}>
              <div className="dia-nome">
                <strong>{t.nome}</strong>
                <small>{doTecnico.length} OS{ocupado ? ` · ${duracaoTexto(ocupado)}` : ''}{t.placa ? ` · ${t.placa}` : ''}</small>
              </div>
              <div className={`dia-trilho${aoClicarVazio ? ' clicavel' : ''}`} onClick={(e) => clicarVazio(e, t.id)} style={{ height: `${Math.max(1, faixas) * ALTURA_FAIXA + 12}px` }}
                title={aoClicarVazio ? 'Clique num horário livre para abrir uma OS para este técnico' : undefined}>
                {horas.map((h) => <i key={h} className="marca-hora" style={{ left: pos(h) }} />)}
                {mesmoDia(dia, agora) && minAgora >= ini && minAgora <= fim && <i className="marca-agora" style={{ left: pos(minAgora) }} />}
                {doTecnico.map((o) => {
                  const d = new Date(o.agendada_para!);
                  const m = d.getHours() * 60 + d.getMinutes();
                  const dur = o.janela_min ?? 60;
                  return (
                    <button key={o.id} className={`bloco-os st-${o.status} prioridade-${o.prioridade}`}
                      style={{ left: pos(m), width: `calc(${(dur / total) * 100}% - 3px)`, top: `${6 + faixa.get(o.id)! * ALTURA_FAIXA}px`, height: `${ALTURA_FAIXA - 6}px` }}
                      onClick={() => aoClicarOs(o)} title={`${o.numero} · ${hora(o.agendada_para!)} · ${o.cliente} — ${o.local}\n${o.descricao}`}>
                      <strong>{o.local}</strong>
                      <span>{hora(o.agendada_para!)} · {o.cliente}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ALTURA_FAIXA = 56;
/** OS que se sobrepõem no mesmo técnico vão para faixas diferentes (em vez de uma cobrir a outra) */
function empilhar(lista: OsAgenda[]) {
  const ordenadas = [...lista].sort((a, b) => Date.parse(a.agendada_para!) - Date.parse(b.agendada_para!));
  const fimDaFaixa: number[] = [];
  const faixa = new Map<string, number>();
  for (const o of ordenadas) {
    const ini = Date.parse(o.agendada_para!), fim = ini + (o.janela_min ?? 60) * 60_000;
    let f = fimDaFaixa.findIndex((x) => x <= ini);
    if (f < 0) { f = fimDaFaixa.length; fimDaFaixa.push(fim); } else fimDaFaixa[f] = fim;
    faixa.set(o.id, f);
  }
  return { faixa, faixas: fimDaFaixa.length };
}

// ---------- Semana: técnicos × 7 dias ----------
function VisaoSemana({ inicio, tecnicos, os, aoClicarOs, aoClicarDia }: {
  inicio: Date; tecnicos: Tecnico[]; os: OsAgenda[]; aoClicarOs: (o: OsAgenda) => void; aoClicarDia: (d: Date) => void;
}) {
  const dias = Array.from({ length: 7 }, (_, i) => somaDias(inicio, i));
  const hoje = new Date();
  return (
    <div className="semana-rolagem">
      <table className="semana">
        <thead>
          <tr>
            <th>Técnico</th>
            {dias.map((d) => <th key={d.getTime()} className={mesmoDia(d, hoje) ? 'hoje' : ''}><button className="btn-link" onClick={() => aoClicarDia(d)}>{diaCurto(d)}</button></th>)}
          </tr>
        </thead>
        <tbody>
          {tecnicos.map((t) => (
            <tr key={t.id}>
              <th scope="row">{t.nome}</th>
              {dias.map((d) => {
                const doDia = os.filter((o) => o.responsavel_id === t.id && mesmoDia(new Date(o.agendada_para!), d));
                return (
                  <td key={d.getTime()} className={mesmoDia(d, hoje) ? 'hoje' : ''}>
                    {doDia.map((o) => (
                      <button key={o.id} className={`mini-os st-${o.status} prioridade-${o.prioridade}`} onClick={() => aoClicarOs(o)} title={`${o.numero} · ${o.descricao}`}>
                        <strong>{hora(o.agendada_para!)}</strong> {o.local}<small>{o.cliente}</small>
                      </button>
                    ))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- OS: resumo + agendar / remarcar / desagendar ----------
export function PainelOs({ os, tecnicos, cfg, podeAgendar, aoSalvar, aoCancelar }: {
  os: OsAgenda; tecnicos: Tecnico[]; cfg: ConfigAgenda; podeAgendar: boolean;
  aoSalvar: (texto: string, conflitos?: Conflito[]) => void; aoCancelar: () => void;
}) {
  const quando = os.agendada_para ? new Date(os.agendada_para) : null;
  const [f, setF] = useState({
    responsavel_id: os.responsavel_id ?? '', data: quando ? isoDia(quando) : os.data_sugerida && os.data_sugerida >= isoDia(new Date()) ? os.data_sugerida : isoDia(new Date()), hora: quando ? hhmm(quando) : '08:00',
    janela_min: String(os.janela_min ?? cfg.duracao_padrao_min),
  });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const editavel = podeAgendar && ['aberta', 'agendada'].includes(os.status);

  async function agendar(e: FormEvent) {
    e.preventDefault(); setErro(null); setSalvando(true);
    try {
      const r = await api<{ conflitos: Conflito[] }>(`/os/${os.id}/agendar`, { metodo: 'POST', corpo: {
        versao: os.versao, responsavel_id: f.responsavel_id, agendada_para: juntarDataHora(f.data, f.hora), janela_min: Number(f.janela_min) } });
      aoSalvar(`${os.numero} ${os.status === 'agendada' ? 'remarcada' : 'agendada'}.`, r.conflitos);
    } catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível agendar.'); }
    finally { setSalvando(false); }
  }
  async function desagendar() {
    setErro(null); setSalvando(true);
    try { await api(`/os/${os.id}/desagendar`, { metodo: 'POST', corpo: { versao: os.versao } }); aoSalvar(`${os.numero} voltou para "a agendar".`); }
    catch (err) { setErro(err instanceof Error ? err.message : 'Não foi possível desagendar.'); }
    finally { setSalvando(false); }
  }

  return (
    <form className="form" onSubmit={agendar}>
      <div className="resumo-os">
        <div className="meta">
          <span className={`status st-${os.status}`}>{NOME_STATUS_OS[os.status]}</span>
          <span className={`pill-prioridade p-${os.prioridade}`}>{NOME_PRIORIDADE[os.prioridade]}</span>
          <span>{os.tipo}</span>
        </div>
        <p className="descricao-os">{os.descricao}</p>
        <p><Link to={`/os/${os.id}`}>Abrir ficha da OS →</Link></p>
        <p className="com-icone"><MapPin size={15} aria-hidden /> <Link to={`/clientes/${os.cliente_id}`}>{os.cliente}</Link> · {os.local}{os.endereco ? ` — ${os.endereco}` : ''}</p>
        {quando && <p className="com-icone"><Clock size={15} aria-hidden /> {diaLongo(quando)}, {hora(os.agendada_para!)} · {duracaoTexto(os.janela_min)} · {os.responsavel}</p>}
      </div>
      {editavel ? (
        <>
          <h3 className="subtitulo-form">{os.status === 'agendada' ? 'Remarcar' : 'Agendar'}</h3>
          <CamposAgenda f={f} set={set} tecnicos={tecnicos} />
          {erro && <p className="alerta" role="alert">{erro}</p>}
          <div className="acoes-form">
            {os.status === 'agendada' && <button type="button" className="btn-fantasma" onClick={desagendar} disabled={salvando}>Tirar da agenda</button>}
            <span className="espaco" />
            <button type="button" className="btn-fantasma" onClick={aoCancelar}>Fechar</button>
            <button className="btn" disabled={salvando || !f.responsavel_id}>{salvando ? 'Salvando…' : os.status === 'agendada' ? 'Remarcar' : 'Agendar'}</button>
          </div>
        </>
      ) : (
        <>
          {!['aberta', 'agendada'].includes(os.status) && <p className="nota">OS em andamento ou fechada: a agenda não muda mais por aqui.</p>}
          <div className="acoes-form"><button type="button" className="btn-fantasma" onClick={aoCancelar}>Fechar</button></div>
        </>
      )}
    </form>
  );
}
